import { Directive, ElementRef, HostBinding, HostListener, Input, OnChanges, forwardRef, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { calculerMontant } from '../utils/montant-calcul.utils';

@Directive({
  selector: 'input[appMontant]',
  standalone: true,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MontantCalculDirective), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => MontantCalculDirective), multi: true },
  ],
})
export class MontantCalculDirective implements ControlValueAccessor, Validator, OnChanges {
  @Input() min: number | string | null = null;
  @Input() max: number | string | null = null;
  private valeur: number | null = null;
  private erreurCalcul: string | null = null;
  private changer: (valeur: number | null) => void = () => {};
  private toucher: () => void = () => {};
  private revalider: () => void = () => {};

  private readonly element = inject<ElementRef<HTMLInputElement>>(ElementRef);

  @HostBinding('attr.title') get aide(): string {
    return this.messageErreur ?? 'Calculs acceptés : 100+25, 200-30, 12*3, 100/4 ou (100+25)/2.';
  }
  @HostBinding('attr.aria-invalid') get invalide(): string | null {
    return this.messageErreur ? 'true' : null;
  }
  @HostBinding('class.app-montant-invalide') get classeInvalide(): boolean {
    return this.messageErreur !== null;
  }

  private get messageErreur(): string | null {
    if (this.erreurCalcul) return this.erreurCalcul;
    if (this.valeur === null) return null;
    if (this.min !== null && this.min !== '' && this.valeur < Number(this.min)) {
      return `Le montant doit être supérieur ou égal à ${this.min}.`;
    }
    if (this.max !== null && this.max !== '' && this.valeur > Number(this.max)) {
      return `Le montant doit être inférieur ou égal à ${this.max}.`;
    }
    return null;
  }

  ngOnChanges(): void {
    this.actualiserValidite();
    this.revalider();
  }

  writeValue(valeur: number | null): void {
    this.valeur = valeur != null && Number.isFinite(Number(valeur)) ? Number(valeur) : null;
    this.erreurCalcul = null;
    this.element.nativeElement.value = this.valeur === null ? '' : String(this.valeur).replace('.', ',');
    this.actualiserValidite();
  }

  registerOnChange(fn: (valeur: number | null) => void): void { this.changer = fn; }
  registerOnTouched(fn: () => void): void { this.toucher = fn; }
  registerOnValidatorChange(fn: () => void): void { this.revalider = fn; }
  setDisabledState(desactive: boolean): void { this.element.nativeElement.disabled = desactive; }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.erreurCalcul) return { calculMontant: this.erreurCalcul };
    if (control.value === null || control.value === '') return null;
    const valeur = Number(control.value);
    if (!Number.isFinite(valeur)) return { calculMontant: 'Montant invalide.' };
    if (this.min !== null && this.min !== '' && valeur < Number(this.min)) {
      return { min: { min: Number(this.min), actual: valeur } };
    }
    if (this.max !== null && this.max !== '' && valeur > Number(this.max)) {
      return { max: { max: Number(this.max), actual: valeur } };
    }
    return null;
  }

  @HostListener('input') saisir(): void {
    const resultat = calculerMontant(this.element.nativeElement.value);
    this.valeur = resultat.valeur;
    this.erreurCalcul = resultat.erreur;
    this.actualiserValidite();
    // Une saisie invalide efface la valeur précédente du modèle : elle ne peut pas être enregistrée.
    this.changer(this.valeur);
    this.revalider();
  }

  @HostListener('blur') terminer(): void {
    if (!this.messageErreur && this.valeur !== null) {
      this.element.nativeElement.value = String(this.valeur).replace('.', ',');
    }
    this.toucher();
    if (this.messageErreur) this.element.nativeElement.reportValidity();
  }

  private actualiserValidite(): void {
    this.element.nativeElement.setCustomValidity(this.messageErreur ?? '');
  }
}
